import { css } from '@emotion/react';
import { Divider, Link, Typography } from '@mui/material';
import NewsmodeIcon from '../../../../assets/icons/newsmode.svg?react';
import { FC } from 'react';
import { INewsDataModel } from '../../../../data-models/news.data-model';
import { colors } from '../../../../theme/colors';
import { elevatedCardStyleCss } from '../../../../theme/component-styles';
import { useShowSummaryV2 } from '../../../CompanyProfiles2/Summary/useShowSummaryV2';
import { getNewsTime } from '../../utils/getNewsTime';

type Props = {
  news: INewsDataModel;
};

const dot = css`
  width: 0.4rem;
  height: 0.4rem;
  background-color: ${colors.secondary[40]};
  border-radius: 50%;
  align-self: center;
`;

const newsLink = css`
  &:hover {
    text-decoration-color: ${colors.secondary[20]};
  }
`;

/**
 * One line per story, per design: icon, then `publisher: title`, then the author and date pushed
 * right. Everything sits beside the icon on one baseline rather than stacking under it, and the
 * summary sentence and per-row card the V1 row uses are both dropped.
 */
const v2Row = css`
  align-items: center;
  column-gap: 0.5rem;
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) max-content max-content;
  padding: 0.25rem 0;
`;

const v2Meta = css`
  align-items: center;
  display: flex;
  gap: 0.25rem;
  white-space: nowrap;
`;

/** Per design: the 16px `newsmode` glyph on a 24px tinted chip, 4px padding all round. */
const v2IconChip = css`
  align-items: center;
  background-color: ${colors.secondary[10]};
  border-radius: 4px;
  color: ${colors.secondary[50]};
  display: flex;
  flex-shrink: 0;
  height: 1.5rem;
  justify-content: center;
  width: 1.5rem;
`;

export const NewsRow: FC<Props> = ({ news }) => {
  const showSummaryV2 = useShowSummaryV2();
  const { title, date, author, publisher, sentence, url } = news;
  const time = date ? getNewsTime(new Date(date)) : '';

  if (showSummaryV2) {
    const headline = [publisher, title].filter(Boolean).join(': ');

    return (
      <div css={v2Row}>
        <span css={v2IconChip}>
          <NewsmodeIcon />
        </span>
        <Link
          variant='body2'
          color='text.primary'
          href={url}
          target='_blank'
          underline='hover'
          noWrap
          css={newsLink}
          title={headline}
        >
          {headline}
        </Link>
        {author ? (
          <Typography variant='caption' color='text.secondary' css={v2Meta}>
            <Divider orientation='vertical' css={dot} />
            {author}
          </Typography>
        ) : (
          <span />
        )}
        <Typography variant='caption' color='text.secondary' whiteSpace='nowrap'>
          {time}
        </Typography>
      </div>
    );
  }

  return (
    <Typography color='text.primary' component='div' css={elevatedCardStyleCss}>
      <Typography variant='overline' lineHeight={'1.5rem'}>
        {publisher}
      </Typography>
      <Link
        variant='subtitle2'
        color='text.primary'
        href={url}
        target='_blank'
        underline='hover'
        css={newsLink}
      >
        {title}
      </Link>
      <Typography variant='caption'>{sentence}</Typography>
      <>
        <Typography variant='caption' color={'text.secondary'} display={'flex'} gap='0.25rem'>
          {time && time}
          {author && (
            <>
              <Divider orientation='vertical' css={dot} />
              {author}
            </>
          )}
        </Typography>
      </>
    </Typography>
  );
};
